/**
 * Public Route Constants
 *
 * Centralized route paths for all public-facing pages.
 * Similar to DASHBOARD_ROUTES but for customer-facing pages.
 *
 * @example
 * // Event routes
 * PUBLIC_ROUTES.EVENTS.detail("my-event-slug")  // "/my-event-slug"
 * PUBLIC_ROUTES.EVENTS.seating("my-event-slug") // "/my-event-slug/seating"
 *
 * @example
 * // Membership routes
 * PUBLIC_ROUTES.MEMBERSHIPS.detail("my-membership") // "/m/my-membership"
 *
 * @example
 * // Checkout
 * PUBLIC_ROUTES.CHECKOUT.ROOT // "/checkout/"
 */
export const PUBLIC_ROUTES = {
  /**
   * Event routes
   * Legacy format: /[slug]/
   * The page component decides whether to render legacy or branded UI
   */
  EVENTS: {
    detail: (slug: string): string => `/${slug}/`,
    seating: (slug: string): string => `/${slug}/seating/`,
    ticketPackage: (slug: string): string => `/${slug}/ticket-package/`,
  },

  /**
   * Membership routes
   * Legacy format: /m/[slug]/
   * The page component decides whether to render legacy or branded UI
   */
  MEMBERSHIPS: {
    detail: (slug: string): string => `/m/${slug}/`,
    seating: (slug: string): string => `/m/${slug}/seating/`,
  },

  /**
   * Checkout routes
   * Single entry point - the page decides what to render based on waffle flag
   */
  CHECKOUT: {
    ROOT: "/checkout/",
    hold: (id: string): string => `/checkout/hold/${id}/`,
    link: (id: string): string => `/checkout/link/${id}/`,
    transfer: (transactionId: string): string =>
      `/checkout/transfers/${transactionId}/`,
    protectionUpsell: (token: string): string =>
      `/checkout/refund-protection/upsell/${token}/`,
  },

  /**
   * Organization profile routes
   */
  ORG_PROFILE: {
    detail: (slug: string): string => `/o/${slug}/`,
  },

  /**
   * Account routes
   */
  ACCOUNT: {
    ROOT: "/account/",
    ORDERS: "/account/orders/",
    MEMBERSHIPS: "/account/memberships/",
    UPCOMING: "/account/upcoming/",
    transaction: (transactionId: string): string =>
      `/account/my-orders/${transactionId}/`,
  },

  /**
   * Discovery routes
   */
  DISCOVER: {
    ROOT: "/",
    SEARCH: "/s/",
  },

  /**
   * Client site routes (custom websites built with Puck editor)
   * Format: /site/[slug] for homepage, /site/[slug]/[page] for other pages
   */
  SITES: {
    BASE_PATH: "/site",
    detail: (slug: string): string => `/site/${slug}`,
    page: (slug: string, pageSlug: string): string =>
      `/site/${slug}/${pageSlug}`,
  },
  PURCHASE_DYNAMIC: {
    ROOT: "/purchase-dynamic/",
  },
} as const;

/**
 * Branded public routes.
 *
 * These routes are can be branded by the venue.
 * If we are not on these routes, branding will be ignored and default showpass theme will be used.
 *
 * @see @file PageThemeConfigContext.tsx
 */
export const BRANDED_PUBLIC_PATHNAMES = [
  `${PUBLIC_ROUTES.CHECKOUT.ROOT}`,
  `${PUBLIC_ROUTES.CHECKOUT.hold("[id]")}`,
  `${PUBLIC_ROUTES.CHECKOUT.link("[id]")}`,
  `${PUBLIC_ROUTES.EVENTS.detail("[eventSlug]")}`,
  `${PUBLIC_ROUTES.EVENTS.seating("[eventSlug]")}`,
  `${PUBLIC_ROUTES.EVENTS.ticketPackage("[eventSlug]")}`,
  `${PUBLIC_ROUTES.MEMBERSHIPS.detail("[membershipSlug]")}`,
  `${PUBLIC_ROUTES.MEMBERSHIPS.seating("[membershipSlug]")}`,
  "/site/[slug]/[[...page]]/",
  `${PUBLIC_ROUTES.PURCHASE_DYNAMIC.ROOT}`,
] as const;
